/**
 * Action Utils - Helpers for the Actions file
 */

import { isUndefined } from 'lodash'
import invariant from 'invariant'

/**
 * We only get fetch the new amount if the amount is valid and one of the following are true:
 * - The previousAmount is specified and different from the amount
 * - The previousCurrencyCode is specified and different from the currencyCode
 *
 * If the amount is what changed, then the previousAmount should be specified.
 * If the currency code is what changed, then the previousCurrencyCode should be specified.
 *
 * @param {String} amount - the new amount (with US formatting)
 * @param {String} previousAmount - the previous amount (with US formatting) if that's what changed
 * @param {String} currencyCode - the currencyCode of the amount
 * @param {String} previousCurrencyCode - the previous currency code if that's what changed
 * @return {Boolean} whether the data provided should be used to fetch a new amount
 */
function shouldFetchNewAmount(
  amount,
  previousAmount,
  currencyCode,
  previousCurrencyCode
) {
  invariant(
    typeof amount === 'string' || typeof amount === 'undefined',
    'Amount should always be a string'
  )
  invariant(
    typeof previousAmount === 'string' || typeof previousAmount === 'undefined',
    'Amount should always be a string'
  )
  invariant(
    arguments.length >= 4,
    'shouldFetchNewAmount is missing required arguments'
  )

  // parse the amount to make sure it seems legit
  const amountNumber = parseFloat(amount)
  const isValidAmount = amountNumber > 0

  // amount changed if the previous amount was undefined
  // or if the new amount is diff than the old amount
  const amountDiffers = amountNumber !== parseFloat(previousAmount)
  const amountChanged = isUndefined(previousAmount) || amountDiffers

  // also check if the currency code changed
  const currencyCodeDiffers = currencyCode !== previousCurrencyCode
  const currencyCodeChanged =
    isUndefined(previousCurrencyCode) || currencyCodeDiffers

  // if the new amount/code are valid and at least one thing changed, we should fetch
  const hasValidAmountAndCode = isValidAmount && currencyCode
  const shouldFetch =
    hasValidAmountAndCode && (amountChanged || currencyCodeChanged)

  return shouldFetch
}

/**
 * returns true if the given amount string is 0
 * @param {String} amount the amount to test
 * @return {Boolean} whether it's 0
 */
function isZero(amount: string) {
  return Number(amount.replace(/\D+/g, '')) === 0
}

// all exports should go here (at the bottom) to make finding the exposed API easier
export { shouldFetchNewAmount, isZero }
